import { useTranslation } from "react-i18next";
import { Badge } from "@components/Badge/Badge";
import { BankButton } from "@components/BankButton/BankButton";
import { BankButtonSkeleton } from "@components/BankButton/BankButtonSkeleton";
import { ExpandingContainer } from "@components/ExpandingContainer/ExpandingContainer";
import { ChartIcon } from "@components/icons";
import { DummyRadio } from "@components/inputs/Radio/DummyRadio";
import { Skeleton } from "@components/Skeleton/Skeleton";
import { useFeatureConfig } from "@hooks/useFeatureFlags";
import {
  usePOASignedDocumentMetadataQuery,
  usePOAStatementConsentsBanksQuery,
} from "@services/powersOfAttorney/queries";
import { AddBillMethod } from "../AddBillVariantSelectorModalTypes";

interface IAddBillBankMethodCardProps {
  variant: AddBillMethod;
  selectedMethod: AddBillMethod | null;
  onClick: () => void;
  selectedBankId: number | undefined;
  setSelectedBankId: (bankId?: number) => void;
  setBillMethod: (method: AddBillMethod | null) => void;
}

export const AddBillBankMethodCard: React.FC<IAddBillBankMethodCardProps> = (
  props,
) => {
  const {
    variant,
    selectedMethod,
    onClick,
    selectedBankId,
    setSelectedBankId,
    setBillMethod,
  } = props;
  const { t } = useTranslation();
  const { isPoaEnabled } = useFeatureConfig();

  // const { data: POAState } = usePOAStatementConsentsStateQuery();
  const { data: POAdocumentMetadata, isLoading: isPOALoading } =
    usePOASignedDocumentMetadataQuery(isPoaEnabled);
  const { data: banksData, isLoading } = usePOAStatementConsentsBanksQuery();

  const poaExists = POAdocumentMetadata?.succeeded;

  const onPOABankSelect = (bankId: number) => {
    if (selectedBankId === bankId) {
      setBillMethod(AddBillMethod.BANK);
      setSelectedBankId(undefined);
      return;
    }
    setBillMethod(null);
    setSelectedBankId(bankId);
  };

  if (isPOALoading) {
    return <Skeleton className="h-[102px] rounded-2xl" />;
  }

  return (
    <div className="divide-y divide-grey-light rounded-2xl border border-grey-light p-6">
      <button
        className="flex w-full items-center gap-4 pb-6 text-left"
        onClick={onClick}
      >
        <Badge icon={ChartIcon} variant="rounded" size="large" color="blue" />
        <div className="flex flex-col gap-1">
          <span className="text-sm font-semibold">
            {t(`addBillVariantSelectorModal.methods.${variant}.title`)}
          </span>
          <span className="text-sm text-grey-dark">
            {poaExists
              ? t(
                  `addBillVariantSelectorModal.methods.${variant}.existsDescription`,
                )
              : t(`addBillVariantSelectorModal.methods.${variant}.description`)}
          </span>
        </div>
        <div className="flex flex-grow justify-end">
          <DummyRadio checked={selectedMethod === variant} />
        </div>
      </button>
      {!poaExists && (
        <div className="pt-6">
          <span className="text-sm font-semibold">
            {t("addBillVariantSelectorModal.selectBank")}
          </span>
          <div className="grid grid-cols-3 gap-4 pt-3">
            {isLoading &&
              Array.from({ length: 7 }).map((_, index) => (
                <BankButtonSkeleton key={index} />
              ))}
            {banksData?.banks.map((bank) => (
              <BankButton
                key={bank.bankId}
                bankName={bank.bankName}
                isSelected={selectedBankId === bank.bankId}
                onClick={() => {
                  setBillMethod(AddBillMethod.BANK);
                  setSelectedBankId(bank.bankId);
                }}
              />
            ))}
          </div>
        </div>
      )}
      {poaExists && (
        <ExpandingContainer
          buttonLabel={t("addBillVariantSelectorModal.selectOtherBank")}
          className="pt-6"
          isEnabled
        >
          <div className="grid grid-cols-3 gap-4 pt-3">
            {isLoading &&
              Array.from({ length: 7 }).map((_, index) => (
                <BankButtonSkeleton key={index} />
              ))}
            {banksData?.banks.map((bank) => (
              <BankButton
                key={bank.bankId}
                bankName={bank.bankName}
                isSelected={selectedBankId === bank.bankId}
                onClick={() => onPOABankSelect(bank.bankId)}
              />
            ))}
          </div>
        </ExpandingContainer>
      )}
    </div>
  );
};
